/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Dec 19 2023
 **/

import { Theme, css } from "@emotion/react";
import styled from "@emotion/styled";
import { ZIndex } from "@/frameworks/core/commons";

import { Row } from "@/frameworks/designStuff/components";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";

export const NoticeLayout = styled(Row)<{ isOpened: boolean; variant: ChatUI.Size }>`
	background-color: ${({ theme }) => theme.colors.neutral_00_100};
	padding: 12px 10px;

	${({ variant, isOpened, theme }) => {
		switch (variant) {
			case "fullSize": {
				return FullSizeChatNoticeCSS({ isOpened, theme });
			}
			case "miniPopup": {
				return MiniChatNoticeCSS({ isOpened, theme });
			}
			case "transparent": {
				return SpaceChatNoticeCSS({ isOpened, theme });
			}
		}
	}}
	.notice {
		width: 100%;
		${({ isOpened }) =>
			!isOpened &&
			`
			width: 100%;
			max-height: 20px;
			overflow-y: hidden;
		`};
		${({ isOpened }) => !isOpened && overflowCss};
		line-height: 20px;
		.og-tag {
			${({ isOpened }) => !isOpened && "display: none;"};
		}
	}
	${({ isOpened, variant }) => isOpened && variant !== "transparent" && hoverScrollCss()}
	.notice_chat_renderer_wrapper {
		width: calc(100% - 32px - 16px);
		height: 100%;
		${({ isOpened, variant }) => !isOpened && variant !== "transparent" && overflowCss}
		.w-100 {
			line-height: 20px;
			${({ isOpened }) => !isOpened && overflowCss}
		}
	}
`;

const overflowCss = css`
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`;

const MiniChatNoticeCSS = ({ theme, isOpened }: { theme: Theme; isOpened: boolean }) => css`
	width: 100%;
	top: 0px;
	left: 0px;
	z-index: ${ZIndex.CHAT_POPUP + 1};
	border-bottom: 1px solid ${theme.colors.neutral_50_20};
	height: ${isOpened ? "auto" : "44px"};
	max-height: 350px;
`;
const FullSizeChatNoticeCSS = ({ isOpened }: { theme: Theme; isOpened: boolean }) => css`
	top: 5px;
	left: 0;
	width: 100%;
	z-index: ${ZIndex.POPUP};
	border-radius: 8px;
	height: ${isOpened ? "auto" : "44px"};
	max-height: 350px;
`;
const SpaceChatNoticeCSS = ({ theme, isOpened }: { theme: Theme; isOpened: boolean }) => css`
	position: ${isOpened ? "absolute" : "relative"};
	${isOpened && "z-index: 2;"}
	width: calc(100% - 8px);
	background: ${theme.colors.gradient_40_100_w80};
	border-radius: 12px;
	box-shadow:
		0px 0px 4px 0px rgba(0, 0, 0, 0.04),
		0px 8px 16px 0px rgba(0, 0, 0, 0.2);
	height: ${isOpened ? "auto" : "44px"};
	bottom: 0;
	max-height: 350px;
`;

export const ChatContent = styled(Row)<{ isOpened: boolean }>`
	width: 100%;
	max-height: 280px;
	height: 100%;
	overflow-y: ${({ isOpened }) => (isOpened ? "auto" : "hidden")};
	&::-webkit-scrollbar-thumb {
		background: ${({ theme }) => theme.colors.neutral_50_40};
		border-radius: 5px;
		background-clip: padding-box;
	}
	&::-webkit-scrollbar-track {
		background: none;
		background-clip: padding-box;
		-webkit-box-shadow: none;
	}
	&::-webkit-scrollbar {
		background-color: none;
	}
`;
